// react_frontend/src/Components/Modules/PurchaseSetting/StockList.js
import React, { useState, useEffect, useMemo, useContext } from "react";
import Breadcrumb from "../../Partials/Breadcrumb";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";
import { MotherContext } from "../../../Context/MotherContext";

// RSuite Components
import {
  Button,
  Input,
  Badge,
  Loader,
  Whisper,
  Tooltip,
} from "rsuite";
import "rsuite/dist/rsuite.min.css";

// Icons
import {
  FaBox,
  FaTags,
  FaSearch,
  FaList,
} from "react-icons/fa";

const StockList = () => {
  const { inventory, loadingInventory, startInventoryPolling, stopInventoryPolling } = useContext(MotherContext);
  const [search, setSearch] = useState("");

  useEffect(() => {
    startInventoryPolling();
    return () => {
      stopInventoryPolling();
    };
  }, [startInventoryPolling, stopInventoryPolling]);

  const filteredData = useMemo(() => {
    return inventory.filter((item) =>
      (item.category?.category_name || "")
        .toLowerCase()
        .includes(search.toLowerCase()) ||
      (item.item?.item_name || "")
        .toLowerCase()
        .includes(search.toLowerCase()) ||
      (item.receipt_no || "").toLowerCase().includes(search.toLowerCase())
    );
  }, [inventory, search]);

  const columns = [
    {
      name: "SL",
      cell: (row, index) => <strong>{index + 1}</strong>,
      width: "100px",
      center: true,
    },
    {
      name: "Category",
      selector: (row) => row.category?.category_name,
      sortable: true,
      width: "250px",
      cell: (row) => (
        <Badge color="blue" style={{ fontSize: "0.85em" }}>
          <FaTags /> {row.category?.category_name || "N/A"}
        </Badge>
      ),
    },
    {
      name: "Item Name",
      selector: (row) => row.item?.item_name,
      sortable: true,
      grow: 2,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FaBox className="text-primary" />
          <span className="fw-medium">{row.item?.item_name || "N/A"}</span>
        </div>
      ),
    },
    {
      name: "Main Stock (QTY)",
      selector: (row) => row.main_stock,
      sortable: true,
      width: "160px",
      cell: (row) => (
        <span className="text-muted small">
          {row.main_stock} {row.product_unit}
        </span>
      ),
    },
    {
      name: "Available Stock (QTY)",
      selector: (row) => row.available_stock,
      sortable: true,
      width: "160px",
      cell: (row) => {
        const available = row.available_stock;
        return (
          <div className="d-flex align-items-center gap-2">
            <Badge
              color={available > 0 ? "green" : "red"}
              content={`${available} ${row.product_unit}`}
              style={{ fontSize: "0.85em" }}
            />
          </div>
        );
      },
    },
    {
      name: "Stock Out (QTY)",
      selector: (row) => row.stock_out,
      sortable: true,
      width: "160px",
      cell: (row) => (
        <Badge color="red" style={{ fontSize: "0.8em" }}>
          {row.stock_out}
        </Badge>
      ),
    },
    {
      name: "Sell out QTY",
      selector: (row) => row.used_qty,
      sortable: true,
      width: "160px",
      cell: (row) => (
        <Badge color="orange" style={{ fontSize: "0.8em" }}>
          {row.used_qty}
        </Badge>
      ),
    },
    {
      name: "Has Product (QTY)",
      selector: (row) => row.has_product,
      sortable: true,
      width: "160px",
      cell: (row) => (
        <Badge color="success" style={{ fontSize: "0.8em" }}>
          {row.has_product}
        </Badge>
      ),
    },
  ];

  const customStyles = {
    head: {
      style: {
        fontSize: "14px",
        fontWeight: "bold",
        backgroundColor: "#f8f9fa",
      },
    },
    rows: {
      style: {
        fontSize: "14px",
        minHeight: "60px",
      },
    },
    cells: {
      style: {
        padding: "12px 8px",
      },
    },
  };

  const StockOutListButton = () => (
    <Whisper placement="top" speaker={<Tooltip>View Stock Out History</Tooltip>}>
      <Link to="/stock_out_list" style={{ textDecoration: "none" }}>
        <Button
          appearance="primary"
          startIcon={<FaList />}
          className="d-flex align-items-center custom-bg"
        >
          Stock Out List
        </Button>
      </Link>
    </Whisper>
  );

  return (
    <>
      <Breadcrumb title="Stock List" />
      <div className="p-3 bg-white rounded shadow-sm mb-4 d-flex flex-wrap gap-3 align-items-center justify-content-between">
        <h5 className="text-dark mb-0">
          <FaBox className="me-2" />
          Current Inventory
        </h5>
        <div className="d-flex gap-2 w-100 w-md-auto align-items-center">
          <Input
            placeholder="Search by item, category, or receipt..."
            value={search}
            onChange={setSearch}
            prefix={<FaSearch />}
            style={{ width: 300 }}
            size="md"
          />
          <StockOutListButton />
        </div>
      </div>
      <DataTable
        keyField="id"
        columns={columns}
        data={filteredData}
        pagination
        paginationPerPage={100}
        paginationRowsPerPageOptions={[100, 250, 500, 1000]}
        progressPending={loadingInventory}
        progressComponent={
          <div className="d-flex justify-content-center my-5">
            <Loader size="md" /> <span className="ms-2">Loading inventory...</span>
          </div>
        }
        noDataComponent={
          <div className="text-center my-5 text-muted">
            <FaBox size={48} className="mb-3 text-secondary" />
            <h5>No inventory found</h5>
            <p>Try adjusting your search or add new stock.</p>
          </div>
        }
        highlightOnHover
        responsive
        fixedHeader
        fixedHeaderScrollHeight="600px"
        customStyles={customStyles}
        striped
      />
    </>
  );
};

export default StockList;